import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import { AxiosResponse } from "axios";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import {
  VOUCHER_TEMPLATES,
  OCCASIONS,
  voucherThumb,
  discountForCount,
  activeTier,
  formatMoney,
  TEAM_PLANS_THRESHOLD,
} from "./data";

type Plan = "single" | "all-access";

interface Recipient {
  name: string;
  email: string;
}

interface Quotation {
  id: number;
  amount: number; // total payable, in currency units (already localised)
  currency: string; // symbol, e.g. "£"
  currency_code: string; // ISO, e.g. "GBP"
  priceable_count: number;
}

interface Course {
  slug: string;
  name: string;
  sample?: boolean; // free/sample course — not giftable
}

interface State {
  loading: boolean;
  plan: Plan;
  courseSlug: string;
  courses: Course[];
  senderName: string;
  senderEmail: string;
  recipients: Recipient[];
  template: string;
  message: string;
  occasion: string;
  sendWhen: "now" | "later";
  scheduleAt: string;
  teamOn: boolean;
  teamName: string;
  quotation: Quotation | null;
  quoting: boolean;
  submitting: boolean;
  redirectTo: string | null;
  errors: string[];
}

const MAX_MESSAGE = 320;

class GiftPage extends Component<{}, State> {
  quoteSeq = 0;
  // Optional ?course=<slug> preselects a course — used by the "Gift this"
  // buttons on course sales pages and completed enrolments.
  courseParam: string | null = new URLSearchParams(window.location.search).get(
    "course",
  );

  state: State = {
    loading: true,
    plan: "single",
    courseSlug: this.courseParam || "",
    courses: [],
    senderName: "",
    senderEmail: "",
    recipients: [{ name: "", email: "" }],
    template: "whale",
    message: "",
    occasion: "all",
    sendWhen: "now",
    scheduleAt: this.defaultScheduleAt(),
    teamOn: false,
    teamName: "",
    quotation: null,
    quoting: false,
    submitting: false,
    redirectTo: null,
    errors: [],
  };

  // Tomorrow at 09:00 local, formatted for <input type="datetime-local">.
  defaultScheduleAt(): string {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  }

  componentDidMount() {
    getRequest(`/api/courses/ranked`)
      .then((res: AxiosResponse<Course[]>) => {
        // Only paid courses are giftable — drop free/sample ones.
        const courses = (res.data || []).filter((c) => !c.sample);
        this.setState(
          { courses, courseSlug: this.courseParam || courses[0]?.slug || "" },
          () => this.loadQuote(),
        );
      })
      .catch(() => this.setState({ loading: false }));
  }

  // Base API path for the currently-selected priceable.
  baseUrl(): string {
    return this.state.plan === "single"
      ? `/api/courses/${this.state.courseSlug}`
      : `/api/passes/year`;
  }

  // Fetch a fresh quotation for the current priceable, then re-price for the
  // current recipient count. The server is the source of truth for the amount.
  loadQuote = () => {
    if (this.state.plan === "single" && !this.state.courseSlug) return;

    const seq = ++this.quoteSeq;
    this.setState({ quoting: true });

    getRequest(`${this.baseUrl()}/gift/new.json`)
      .then((res: AxiosResponse<any>) => {
        if (seq !== this.quoteSeq) return; // a newer request superseded this one
        const quotation = res.data.quotation as Quotation;
        const count = this.state.recipients.length;
        if (count > 1 && quotation?.id) {
          return this.refreshQuote(quotation.id, count, seq);
        }
        this.setState({ quotation, quoting: false, loading: false });
      })
      .catch(() => {
        if (seq === this.quoteSeq)
          this.setState({ quoting: false, loading: false });
      });
  };

  refreshQuote = (quoteId: number, count: number, seq: number) => {
    return getRequest(
      `${this.baseUrl()}/gift/quote.json?quote_id=${quoteId}&count=${count}`,
    )
      .then((res: AxiosResponse<any>) => {
        if (seq !== this.quoteSeq) return;
        this.setState({
          quotation: res.data.quotation as Quotation,
          quoting: false,
          loading: false,
        });
      })
      .catch(() => {
        if (seq === this.quoteSeq)
          this.setState({ quoting: false, loading: false });
      });
  };

  // Re-price for the current recipient count without changing the priceable.
  reprice = () => {
    const { quotation, recipients } = this.state;
    if (!quotation?.id) return this.loadQuote();
    const seq = ++this.quoteSeq;
    this.setState({ quoting: true });
    this.refreshQuote(quotation.id, recipients.length, seq);
  };

  // ---- handlers ----
  selectPlan = (plan: Plan) => {
    if (plan === this.state.plan) return;
    this.setState({ plan }, this.loadQuote);
  };

  selectCourse = (courseSlug: string) => {
    this.setState({ courseSlug }, this.loadQuote);
  };

  updateRecipient = (idx: number, field: keyof Recipient, value: string) => {
    const recipients = this.state.recipients.map((r, i) =>
      i === idx ? { ...r, [field]: value } : r,
    );
    this.setState({ recipients });
  };

  addRecipient = () => {
    this.setState(
      { recipients: [...this.state.recipients, { name: "", email: "" }] },
      this.reprice,
    );
  };

  removeRecipient = (idx: number) => {
    if (this.state.recipients.length <= 1) return;
    this.setState(
      { recipients: this.state.recipients.filter((_, i) => i !== idx) },
      this.reprice,
    );
  };

  // ---- pricing helpers (display only; server amount is authoritative) ----
  unitBase(): number {
    const { quotation } = this.state;
    if (!quotation) return 0;
    const count = quotation.priceable_count || 1;
    const ratio = 1 - discountForCount(count);
    return quotation.amount / (count * ratio);
  }

  proceed = () => {
    const { plan, courseSlug, senderName, senderEmail, recipients, quotation } =
      this.state;

    if (recipients.length >= TEAM_PLANS_THRESHOLD) {
      (window as any).location = "/team_plans?from=gift";
      return;
    }

    const errors: string[] = [];
    if (plan === "single" && !courseSlug)
      errors.push("Choose a course to gift.");
    if (!senderName.trim() || !senderEmail.trim())
      errors.push("Add your name and email.");
    recipients.forEach((r, i) => {
      if (!r.name.trim() || !r.email.trim())
        errors.push(`Add a name and email for recipient ${i + 1}.`);
    });
    if (!quotation?.id)
      errors.push("We couldn't load a price — please try again.");
    if (errors.length) {
      this.setState({ errors });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    this.setState({ submitting: true, errors: [] });

    const body: any = {
      gift: {
        sender_name: senderName,
        sender_email: senderEmail,
        template: this.state.template,
        message: this.state.message,
        quotation_id: quotation.id,
        recipients,
        team_name: this.state.teamOn ? this.state.teamName : "",
      },
    };
    if (this.state.sendWhen === "later") {
      body.gift.deliver_at = this.state.scheduleAt;
      body.gift.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    postRequest(`${this.baseUrl()}/gift`, body)
      .then((res: AxiosResponse<any>) => {
        const token = res.data.token;
        const paid = res.data.state === "paid";
        this.setState({
          redirectTo: `/gift/${token}/${paid ? "success" : "pay"}`,
        });
      })
      .catch((error: any) => {
        const apiErrors = error?.response?.data?.errors;
        const messages: string[] = apiErrors
          ? ([] as any[])
              .concat(...Object.values(apiErrors))
              .map((m) => String(m))
          : ["Something went wrong creating your gift. Please try again."];
        this.setState({ submitting: false, errors: messages });
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
  };

  render() {
    if (this.state.redirectTo) return <Redirect to={this.state.redirectTo} />;

    const {
      loading,
      plan,
      courses,
      courseSlug,
      recipients,
      template,
      occasion,
      quotation,
    } = this.state;

    const count = recipients.length;
    const discount = discountForCount(count);
    const tier = activeTier(count);
    const symbol = quotation?.currency || "£";
    const code = quotation?.currency_code || "";
    const unit = this.unitBase();
    const planName =
      plan === "all-access" ? "All Access Pass" : "Single Mini MBA";
    const subtotal = unit * count;
    const total = quotation?.amount ?? subtotal;

    const visibleVouchers = VOUCHER_TEMPLATES.filter(
      (v) => occasion === "all" || v.occasions.includes(occasion),
    );

    return (
      <main className="gift-page">
        <section className="gp-hero">
          <div className="gp-container gp-hero-inner">
            <span className="gp-eyebrow">Gift vouchers</span>
            <h1>
              Give a gift that <em>compounds.</em>
            </h1>
            <p className="gp-tagline">
              Most gifts are forgotten in a fortnight. A Mini MBA quietly keeps
              paying out for years.
            </p>
            <p className="gp-wink">
              The path less travelled by flowers and chocolates.
            </p>
          </div>
        </section>

        <section className="gp-main">
          <div className="gp-container gp-grid">
            <div className="gp-form-col">
              {this.state.errors.length > 0 && (
                <div className="gp-errors" role="alert">
                  <strong>Just a few bits missing:</strong>
                  <ul>
                    {this.state.errors.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* STEP 1 — PLAN */}
              <div className="gp-card">
                <div className="gp-step-head">
                  <span className="gp-step-num">1</span>
                  <h2>What are you giving?</h2>
                </div>
                <div
                  className="gp-plan-toggle"
                  role="radiogroup"
                  aria-label="Choose plan"
                >
                  <button
                    type="button"
                    className={`gp-plan ${plan === "single" ? "selected" : ""}`}
                    role="radio"
                    aria-checked={plan === "single"}
                    onClick={() => this.selectPlan("single")}
                  >
                    <span className="gp-plan-title">Single Mini MBA</span>
                    <span className="gp-plan-desc">
                      Pick one CPD-accredited course, owned forever.
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`gp-plan ${plan === "all-access" ? "selected" : ""}`}
                    role="radio"
                    aria-checked={plan === "all-access"}
                    onClick={() => this.selectPlan("all-access")}
                  >
                    <span className="gp-plan-title">All Access Pass</span>
                    <span className="gp-plan-desc">
                      Every Mini MBA for a year, plus the Community Hub.
                    </span>
                  </button>
                </div>

                {plan === "single" && (
                  <div className="gp-course-picker">
                    <label htmlFor="gp-course">Which course?</label>
                    <select
                      id="gp-course"
                      value={courseSlug}
                      onChange={(e) => this.selectCourse(e.target.value)}
                    >
                      {courses.map((c) => (
                        <option key={c.slug} value={c.slug}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Pricing ladder */}
                <div className="gp-ladder">
                  <div className="gp-ladder-head">
                    <span className="gp-ladder-title">Better with more</span>
                    <span className="gp-ladder-sub">
                      Discount applies automatically as you add recipients
                    </span>
                  </div>
                  <div className="gp-ladder-tiers">
                    <div className={`gp-tier ${tier === 1 ? "active" : ""}`}>
                      <span className="gp-tier-count">1 person</span>
                      <span className="gp-tier-price">
                        {unit ? `${formatMoney(unit, symbol)} each` : "—"}
                      </span>
                    </div>
                    <div className={`gp-tier ${tier === 2 ? "active" : ""}`}>
                      <span className="gp-tier-count">2 to 3</span>
                      <span className="gp-tier-price">
                        {unit
                          ? `${formatMoney(unit * 0.85, symbol)} each`
                          : "—"}
                      </span>
                    </div>
                    <div className={`gp-tier ${tier === 3 ? "active" : ""}`}>
                      <span className="gp-tier-count">4 to 9</span>
                      <span className="gp-tier-price">
                        {unit
                          ? `${formatMoney(unit * 0.75, symbol)} each`
                          : "—"}
                      </span>
                    </div>
                  </div>
                  <p className="gp-ladder-note">
                    Got <strong>ten or more</strong>? Our{" "}
                    <a href="/team_plans">Team plans</a> offer bespoke pricing,
                    group analytics, and onboarding.
                  </p>
                </div>
              </div>

              {/* STEP 2 — WHO */}
              <div className="gp-card">
                <div className="gp-step-head">
                  <span className="gp-step-num">2</span>
                  <h2>Who's it for?</h2>
                </div>

                <div className="gp-block-title">From</div>
                <div className="gp-input-row">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={this.state.senderName}
                    onChange={(e) =>
                      this.setState({ senderName: e.target.value })
                    }
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={this.state.senderEmail}
                    onChange={(e) =>
                      this.setState({ senderEmail: e.target.value })
                    }
                  />
                </div>

                <div className="gp-block-title gp-mt">To</div>
                {recipients.map((r, i) => (
                  <div className="gp-recipient" key={i}>
                    <div className="gp-recipient-head">
                      <span>Recipient {i + 1}</span>
                      {recipients.length > 1 && (
                        <button
                          type="button"
                          className="gp-remove"
                          aria-label={`Remove recipient ${i + 1}`}
                          onClick={() => this.removeRecipient(i)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                    <div className="gp-input-row">
                      <input
                        type="text"
                        placeholder="Their name"
                        value={r.name}
                        onChange={(e) =>
                          this.updateRecipient(i, "name", e.target.value)
                        }
                      />
                      <input
                        type="email"
                        placeholder="Their email"
                        value={r.email}
                        onChange={(e) =>
                          this.updateRecipient(i, "email", e.target.value)
                        }
                      />
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  className="gp-add"
                  onClick={this.addRecipient}
                >
                  + Gifting to someone else too? Add them
                </button>

                {count >= TEAM_PLANS_THRESHOLD && (
                  <div className="gp-bulk-route">
                    <strong>That's ten or more recipients.</strong> Our{" "}
                    <a href="/team_plans">Team plans</a> are built for this —
                    bespoke pricing, group analytics, and a human to help set it
                    up.
                  </div>
                )}
              </div>

              {/* STEP 3 — VOUCHER */}
              <div className="gp-card">
                <div className="gp-step-head">
                  <span className="gp-step-num">3</span>
                  <h2>Pick the voucher.</h2>
                </div>
                <div className="gp-occasions" role="tablist">
                  {OCCASIONS.map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className={`gp-chip ${occasion === o.id ? "active" : ""}`}
                      onClick={() => this.setState({ occasion: o.id })}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <div className="gp-voucher-grid">
                  {visibleVouchers.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      className={`gp-voucher ${template === v.id ? "selected" : ""}`}
                      aria-label={`Choose voucher: ${v.label}`}
                      aria-pressed={template === v.id}
                      onClick={() => this.setState({ template: v.id })}
                    >
                      <img
                        src={voucherThumb(v.id)}
                        alt={`${v.label} voucher`}
                        loading="lazy"
                      />
                      {template === v.id && (
                        <span className="gp-voucher-tick">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* STEP 4 — MESSAGE */}
              <div className="gp-card">
                <div className="gp-step-head">
                  <span className="gp-step-num">4</span>
                  <h2>Add a note (optional).</h2>
                </div>
                <textarea
                  className="gp-message"
                  maxLength={MAX_MESSAGE}
                  placeholder={
                    "e.g. Dear Einstein,\nI found you this course so you can be even smarter.\nEnjoy — Marie x"
                  }
                  value={this.state.message}
                  onChange={(e) => this.setState({ message: e.target.value })}
                />
                <div className="gp-message-meta">
                  <span>A one-liner often lands better than a paragraph.</span>
                  <span>
                    {this.state.message.length} / {MAX_MESSAGE}
                  </span>
                </div>
              </div>

              {/* STEP 5 — WHEN */}
              <div className="gp-card">
                <div className="gp-step-head">
                  <span className="gp-step-num">5</span>
                  <h2>When should it land?</h2>
                </div>
                <div className="gp-send-options" role="radiogroup">
                  <button
                    type="button"
                    className={`gp-send ${this.state.sendWhen === "now" ? "selected" : ""}`}
                    role="radio"
                    aria-checked={this.state.sendWhen === "now"}
                    onClick={() => this.setState({ sendWhen: "now" })}
                  >
                    <span className="gp-send-dot" />
                    <span>
                      <span className="gp-send-text">Send now</span>
                      <span className="gp-send-sub">
                        They get it once you've checked out.
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`gp-send ${this.state.sendWhen === "later" ? "selected" : ""}`}
                    role="radio"
                    aria-checked={this.state.sendWhen === "later"}
                    onClick={() => this.setState({ sendWhen: "later" })}
                  >
                    <span className="gp-send-dot" />
                    <span>
                      <span className="gp-send-text">Schedule for later</span>
                      <span className="gp-send-sub">
                        Pick a future date and time.
                      </span>
                    </span>
                  </button>
                </div>
                {this.state.sendWhen === "later" && (
                  <div className="gp-schedule">
                    <label htmlFor="gp-schedule-at">Send at:</label>
                    <input
                      id="gp-schedule-at"
                      type="datetime-local"
                      value={this.state.scheduleAt}
                      onChange={(e) =>
                        this.setState({ scheduleAt: e.target.value })
                      }
                    />
                    <span className="gp-tz">(your local time)</span>
                  </div>
                )}
              </div>

              {/* STEP 6 — TEAM (conditional) */}
              {count >= 2 && count < TEAM_PLANS_THRESHOLD && (
                <div className="gp-card">
                  <div className="gp-step-head">
                    <span className="gp-step-num">6</span>
                    <h2>Make them a team?</h2>
                  </div>
                  <p className="gp-team-intro">
                    Set up a private leaderboard so your recipients can see each
                    other's progress. Free, and slightly addictive.
                  </p>
                  <label className="gp-team-toggle">
                    <input
                      type="checkbox"
                      checked={this.state.teamOn}
                      onChange={(e) =>
                        this.setState({ teamOn: e.target.checked })
                      }
                    />
                    <span>Set up a team</span>
                  </label>
                  {this.state.teamOn && (
                    <div className="gp-team-fields">
                      <label htmlFor="gp-team-name">Team name</label>
                      <input
                        id="gp-team-name"
                        type="text"
                        maxLength={40}
                        placeholder="e.g. The Marketing Brain Trust"
                        value={this.state.teamName}
                        onChange={(e) =>
                          this.setState({ teamName: e.target.value })
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT: PREVIEW + SUMMARY */}
            <aside className="gp-preview-col">
              <div className="gp-preview-card">
                <div className="gp-preview-head">
                  <span>Live preview</span>
                </div>
                <div className="gp-preview-voucher">
                  <img
                    src={voucherThumb(template)}
                    alt="Selected voucher design"
                  />
                </div>
                {this.state.message.trim() && (
                  <div className="gp-preview-message">
                    <span className="gp-pm-label">Your note to them</span>
                    <span className="gp-pm-content">{this.state.message}</span>
                  </div>
                )}

                <div className="gp-pricing">
                  <h3>Pricing</h3>
                  <div className={`gp-price-line ${loading ? "muted" : ""}`}>
                    <span>
                      {planName} &times; {count}
                    </span>
                    <span>
                      {quotation ? formatMoney(subtotal, symbol) : "…"}
                    </span>
                  </div>
                  {discount > 0 && quotation && (
                    <div className="gp-price-line discount">
                      <span>
                        Volume discount ({Math.round(discount * 100)}%)
                      </span>
                      <span>
                        &minus;{formatMoney(subtotal - total, symbol)}
                      </span>
                    </div>
                  )}
                  <div className="gp-price-line total">
                    <span>Total</span>
                    <span className="gp-total-amount">
                      {quotation ? formatMoney(total, symbol) : "…"}
                      {code && <span className="ccy-code">{code}</span>}
                    </span>
                  </div>
                </div>

                <div className="gp-cta">
                  <button
                    type="button"
                    className="gp-proceed"
                    disabled={this.state.submitting}
                    onClick={this.proceed}
                  >
                    {this.state.submitting
                      ? "Taking you to checkout…"
                      : "Proceed to checkout →"}
                  </button>
                </div>

                <div className="gp-trust">
                  <strong>30-day money-back guarantee.</strong> If they aren't
                  happy, we aren't happy.
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>
    );
  }
}

export default GiftPage;
